/* eslint-disable */
// TODO: The snowplowUrl is an app agnostic setting. Move into a global config?
import qs from 'query-string';
import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { Cookies } from 'react-cookie';
import {
    areCookiesAvailable,
    loadScript,
    sanitizeUUID,
} from 'wanderu-utils';

import PixelStructuredEvent from './PixelStructuredEvent';
import StructuredEvent from './StructuredEvent';
import {
    SnowplowSdkFailedToLoad,
    SnowplowNotInitialized,
} from './errors/SnowplowServiceErrors';

const THIRTY_SECONDS_IN_MS = 30 * 1000;
const ONE_SECONDS_IN_MS = 1 * 1000;
const logger = Logger.getLogger('SnowplowService');
const snowplowCookieName = 'sp';

function _loaded() {
    const snowplow = _getSnowplow();
    return !!snowplow && typeof snowplow === 'function';
}

function _getSnowplow() {
    return window[appSettings.config.snowplow.namespace];
}

let appRegion;
let appVersion;
let appGitHash;

function init(region, version, gitHash) {
    appRegion = region;
    appVersion = version;
    appGitHash = gitHash;
}

function fireStructuredEventPixel(event) {
    const {
        endpoint,
        customPath,
    } = appSettings.config.snowplow;

    const structuredEvent = new PixelStructuredEvent(event);
    const url = `//${endpoint}${customPath.get}?${qs.stringify(structuredEvent.format())}`;
    const pixel = document.createElement('img');
    pixel.setAttribute('src', url);
    pixel.setAttribute('style', 'display: none; visibility: hidden;');
    document.querySelector('body').appendChild(pixel);
}

async function fetchSDK(onload) {
    if (!appSettings.env.isClient) {
        return;
    }

    let snowplow = _getSnowplow();
    if (snowplow) {
        return Promise.resolve();
    }

    const uuidContext = { uuid: window.__uuid };
    try {
        _setupWindow();
        snowplow = _getSnowplow();

        if (onload) {
            // This fn will be called after sp is fully loaded
            snowplow(_waitForSpCookieToBeSet(onload));
        }
        await loadScript(appSettings.config.snowplow.url);

        logger.event('fetchSDK', 'loaded', uuidContext);
        fireStructuredEvent({
            category: 'sp_load',
            action: 'loaded',
            property: window.__uuid,
        });
    } catch (ex) {
        logger.event('fetchSDK', 'failed', uuidContext);
        fireStructuredEventPixel({
            category: 'sp_load',
            action: 'failed',
            property: 'yes_js',
        });
        throw new SnowplowSdkFailedToLoad();
    }
}

function _waitForSpCookieToBeSet(onload) {
    let intervalId;
    const endTime = Date.now() + THIRTY_SECONDS_IN_MS;

    // We need to wait for an event to return so the sp
    // collector can set the network user id cookie via server
    function _checkForSpCookie() {
        const networkUserId = getNetworkUserId();
        const domainUserId = getDomainUserId();
        if (networkUserId && domainUserId) {
            clearInterval(intervalId);
            return onload();
        }

        if (Date.now() > endTime) {
            clearInterval(intervalId);
            onload();
        }
    }

    return function _onSpCookieSet() {
        if (!areCookiesAvailable()) {
            return onload();
        }

        const networkUserId = getNetworkUserId();
        const domainUserId = getDomainUserId();
        if (networkUserId && domainUserId) {
            return onload();
        }

        intervalId = setInterval(_checkForSpCookie, ONE_SECONDS_IN_MS);
    };
}

/* istanbul ignore next */ // TS: this is snowplow's code
function _setupWindow() {
    const { namespace } = appSettings.config.snowplow;

    window.GlobalSnowplowNamespace = window.GlobalSnowplowNamespace || [];
    window.GlobalSnowplowNamespace.push(namespace);
    window[namespace] = function() {
        window[namespace].q = window[namespace].q || [];
        window[namespace].q.push(arguments);
    };
    window[namespace].q = window[namespace].q || [];
}

function createNewTracker(appId, cookieDomain = '.wanderu.com') {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('createNewTracker', ex, { cookieDomain });
    }

    logger.debug('createNewTracker', appId, cookieDomain);

    const snowplow = _getSnowplow();

    snowplow('newTracker', 'cf', appSettings.config.snowplow.endpoint, {
        cookieDomain,
        appId,
        post: !!appSettings.config.snowplow.post,
        postPath: appSettings.config.snowplow.customPath.post,
    });
}

function startTracking() {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex);
    }

    logger.debug('initialize', 'startTracking');
    const snowplow = _getSnowplow();
    snowplow('enableActivityTracking', {
        minimumVisitLength: 5,
        heartbeatDelay: 30,
    });
    snowplow('trackPageView');
    snowplow('enableLinkClickTracking', {
        pseudoClicks: true,
    });
    snowplow('enableFormTracking');
}

interface Context {
    schema: string;
    data: {
        [key: string]: string;
    };
}

function addGlobalContexts(contexts: Context[]) {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex);
    }

    logger.debug('initialize', 'addGlobalContexts');

    const snowplow = _getSnowplow();

    snowplow('addGlobalContexts', contexts);
}

// TS: These event requests might be cancelled on page unload, but as long as
//    they are requested, sp should eventually receive them. https://goo.gl/Ijrq52
function fireStructuredEvent(event) {
    const {
        category,
        action,
        label,
        property,
        value,
    } = event;
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex, {
            category,
            action,
            label,
            property,
            value,
        });
    }

    try {

        const context = [{
            'schema': 'iglu:com.snowplowanalytics.snowplow/contexts/jsonschema/1-0-1',
            'data': [{
                'schema': 'iglu:com.snowplowanalytics.mobile/application/jsonschema/1-0-0',
                'data': {
                    version: appSettings.env.build.version,
                    build: appSettings.env.build.gitHash
                }
            }]
        }];

        const structuredEvent = new StructuredEvent({
            category,
            action,
            label,
            property,
            value,
            context
        });

        const snowplow = _getSnowplow();

        logger.debug('fireStructuredEvent', 'snowplow', structuredEvent);

        snowplow(...structuredEvent.format());
    } catch (ex) {
        return logger.error('fireStructuredEvent', ex, {
            category,
            action,
            label,
            property,
            value
        });
    }
}

function setUserId(userId) {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex, { userId });
    }

    logger.debug('setUserId', userId);

    const snowplow = _getSnowplow();
    snowplow('setUserId', userId);
}


const initBatchId = val => {
    let batchId = val;
    return {
        getValue: () => batchId,
        setValue: newVal => batchId = newVal,
    };
};

let batchId = initBatchId('');

function setBatchId(value) {
    batchId.setValue(value);
}

function fireStructuredEventWithBatchId(event) {
    const {
        property,
        ...rest
    } = event;
    const updatedEvent = {
        ...rest,
        property: {
            ...property,
            batch_id: batchId.getValue(),
        },
    };
    fireStructuredEvent(updatedEvent);
}

function removeUserId() {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex);
    }

    logger.debug('removeUserId');

    const snowplow = _getSnowplow();
    snowplow('setUserId', null);
}

function addTransaction(transaction) {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex, { transaction });
    }

    logger.debug('addTransaction', 'addTrans', transaction);

    if (!transaction) {
        return;
    }

    const snowplow = _getSnowplow();
    snowplow('addTrans', transaction);
}

function addTransactionItem(item) {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex, { item });
    }

    logger.debug('addTransactionItem', item);

    if (!item) {
        return;
    }

    const snowplow = _getSnowplow();
    snowplow('addItem', item);
}

function trackTransaction() {
    try {
        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex);
    }

    logger.debug('trackTransactionFired');

    const snowplow = _getSnowplow();
    snowplow('trackTrans');
}

function _assertSnowplowInitialized() {
    if (!_loaded()) {
        throw new SnowplowNotInitialized();
    }
}

function getNetworkUserId() {
    const cookies = new Cookies()
    return sanitizeUUID(cookies.get(snowplowCookieName));
}

function getDomainUserId() {
    const snowplow = _getSnowplow();
    let domainUserId;
    snowplow(function() {
        domainUserId = this.cf.getDomainUserId();
    });

    return domainUserId;
}

function getDomainUserInfo() {
    const snowplow = _getSnowplow();
    let domainUserInfo = {};
    snowplow(function() {
        if (!this.cf) {
            return domainUserInfo;
        }        
        const [
            , // A string set to '1' if this is the user's first session and '0' otherwise
            domainUserId,
            , // The timestamp at which the cookie was created
            domainSessionIdx,
            , // The timestamp for the current visit
            , // The timestamp of the last visit
            domainSessionId,
        ] = this.cf.getDomainUserInfo();
        domainUserInfo = {
            ...domainUserInfo,
            domainUserId,
            domainSessionId,
            domainSessionIdx: domainSessionIdx.toString(),
        };
    });
    return domainUserInfo;
}

async function getIds() {
    return new Promise((resolve, reject) => {
        const startWaiting = _waitForSpCookieToBeSet(() => {
            const ids = {
                networkUserId: getNetworkUserId(),
                ...getDomainUserInfo(),
            };
            return resolve(ids);
        });

        startWaiting();
    });
}

function trackPageView() {
    try {

        _assertSnowplowInitialized();
    } catch (ex) {
        return logger.warn('initialize', ex);
    }

    logger.debug('trackPageViewTriggered', 'triggerPageView');

    const snowplow = _getSnowplow();
    snowplow('trackPageView');
}

export {
    init,
    addGlobalContexts,
    addTransaction,
    addTransactionItem,
    trackTransaction,
    fetchSDK,
    createNewTracker,
    startTracking,
    fireStructuredEvent,
    fireStructuredEventPixel,
    removeUserId,
    setBatchId,
    setUserId,
    snowplowCookieName,
    SnowplowNotInitialized,
    SnowplowSdkFailedToLoad,
    getNetworkUserId,
    getDomainUserId,
    getDomainUserInfo,
    getIds,
    trackPageView,
    fireStructuredEventWithBatchId,
};

export default {
    init,
    addGlobalContexts,
    addTransaction,
    addTransactionItem,
    trackTransaction,
    fetchSDK,
    createNewTracker,
    startTracking,
    fireStructuredEvent,
    fireStructuredEventPixel,
    removeUserId,
    setBatchId,
    setUserId,
    snowplowCookieName,
    SnowplowNotInitialized,
    SnowplowSdkFailedToLoad,
    getNetworkUserId,
    getDomainUserId,
    getDomainUserInfo,
    getIds,
    trackPageView,
    fireStructuredEventWithBatchId,
};
